/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import {
  Breadcrumbs as AriaBreadcrumbs,
  type BreadcrumbProps,
  type BreadcrumbsProps,
} from 'react-aria-components/Breadcrumbs';
import styles from './styles.module.css';

/**
 * Breadcrumbs - Accessible breadcrumb navigation
 *
 * Displays the user's location within an application hierarchy and provides
 * links back to parent pages. The final item represents the current page.
 *
 * @param props - The breadcrumbs props.
 * @param props.children - BreadcrumbItem components representing the navigation path.
 * @param props.className - Additional CSS class names for styling.
 * @returns The breadcrumbs navigation component.
 *
 * @example
 * ```tsx
 * <Breadcrumbs>
 *   <BreadcrumbItem linkProps={{ href: '/' }}>Home</BreadcrumbItem>
 *   <BreadcrumbItem>Current</BreadcrumbItem>
 * </Breadcrumbs>
 * ```
 */
export function Breadcrumbs({
  children,
  className,
}: BreadcrumbsProps<BreadcrumbProps>) {
  return (
    <AriaBreadcrumbs className={clsx(styles.list, className)}>
      {children}
    </AriaBreadcrumbs>
  );
}
